<script setup lang="ts">
interface Props {
  modelValue: boolean
  maxWidth?: number | string
  persistent?: boolean
  scrollable?: boolean
  blurAmount?: number
}

const props = withDefaults(defineProps<Props>(), {
  maxWidth: 400,
  persistent: false,
  scrollable: false,
  blurAmount: 4,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

// Create a computed property with getter and setter
const isDialogOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

// Apply blur effect when dialog opens
watch(() => isDialogOpen.value, (isOpen) => {
  const appElement = document.querySelector('.v-application')
  if (appElement) {
    if (isOpen) {
      // Apply custom blur style with dynamic blur amount
      const style = document.createElement('style')
      style.id = 'blur-dialog-style'
      style.textContent = `
        .dialog-blur-background > div:not(.v-dialog):not(.v-overlay__content) {
          filter: blur(${props.blurAmount}px);
          transition: filter 0.3s ease;
        }
      `
      document.head.appendChild(style)
      appElement.classList.add('dialog-blur-background')
    }
    else {
      // Remove blur effect
      appElement.classList.remove('dialog-blur-background')
      const styleElement = document.getElementById('blur-dialog-style')
      if (styleElement) {
        styleElement.remove()
      }
    }
  }
})

// Clean up on component unmount
onBeforeUnmount(() => {
  const appElement = document.querySelector('.v-application')
  if (appElement) {
    appElement.classList.remove('dialog-blur-background')
  }
  const styleElement = document.getElementById('blur-dialog-style')
  if (styleElement) {
    styleElement.remove()
  }
})
</script>

<template>
  <v-dialog
    v-model="isDialogOpen"
    :max-width="props.maxWidth"
    :persistent="props.persistent"
    :scrollable="props.scrollable"
    v-bind="$attrs"
  >
    <slot />
  </v-dialog>
</template>
